/**
 * Hooks Notes apprenant — domaine isolé.
 * Pas de logique d'affichage ici, juste l'IO + cache invalidation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';

export type NoteCategory = 'general' | 'commercial' | 'pedagogique' | 'admin' | 'alerte';

export interface ApprenantNote {
  id: string;
  apprenantId: string | null;
  author: { email: string; name: string | null };
  content: string;
  category: NoteCategory;
  pinned: boolean;
  linkedEmailId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoteBody {
  content: string;
  category?: NoteCategory;
  pinned?: boolean;
}

const QK = {
  byApprenant: (apprenantId: string) => ['v2', 'notes', 'apprenant', apprenantId] as const,
  recent: () => ['v2', 'notes', 'recent'] as const,
};

export function useApprenantNotes(apprenantId: string) {
  return useQuery({
    queryKey: QK.byApprenant(apprenantId),
    queryFn: () => api.get<ApprenantNote[]>(`/api/v1/v2/apprenants/${apprenantId}/notes`),
    enabled: !!apprenantId && apprenantId !== 'demo-martine',
  });
}

export function useRecentNotes(enabled = true) {
  return useQuery({
    queryKey: QK.recent(),
    queryFn: () => api.get<ApprenantNote[]>('/api/v1/v2/apprenants/notes/recent'),
    enabled,
  });
}

export function useCreateNote(apprenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateNoteBody) =>
      api.post<ApprenantNote>(`/api/v1/v2/apprenants/${apprenantId}/notes`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.byApprenant(apprenantId) });
      qc.invalidateQueries({ queryKey: QK.recent() });
      qc.invalidateQueries({ queryKey: ['v2', 'communications', 'timeline'] });
    },
  });
}

export function useDeleteNote(apprenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      api.delete(`/api/v1/v2/apprenants/notes/${noteId}`).then(() => noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.byApprenant(apprenantId) });
      qc.invalidateQueries({ queryKey: QK.recent() });
      qc.invalidateQueries({ queryKey: ['v2', 'communications', 'timeline'] });
    },
  });
}

export function useTogglePin(apprenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, pinned }: { noteId: string; pinned: boolean }) =>
      api.patch<ApprenantNote>(`/api/v1/v2/apprenants/notes/${noteId}`, { pinned }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.byApprenant(apprenantId) });
      qc.invalidateQueries({ queryKey: QK.recent() });
    },
  });
}
